import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { logger } from '../utils/logger';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

export class BaseApiService {
  protected client: AxiosInstance;
  private retryConfig: RetryConfig;

  constructor(baseURL: string, defaultHeaders: Record<string, string> = {}) {
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 10000,
    };

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        ...defaultHeaders,
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor for rate limiting and logging
    this.client.interceptors.request.use(
      (config) => {
        logger.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        logger.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling and retries
    this.client.interceptors.response.use(
      (response) => {
        logger.debug(`API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle rate limiting (429) with exponential backoff
        if (error.response?.status === 429 && !originalRequest._retry) {
          return this.handleRateLimit(originalRequest);
        }

        // Handle server errors (5xx) with retry
        if (
          error.response?.status >= 500 &&
          !originalRequest._retry &&
          originalRequest._retryCount < this.retryConfig.maxRetries
        ) {
          return this.handleServerError(originalRequest);
        }

        return Promise.reject(this.formatError(error));
      }
    );
  }

  private async handleRateLimit(originalRequest: any): Promise<AxiosResponse> {
    originalRequest._retry = true;
    const delay = this.calculateBackoffDelay(1);
    
    logger.warn(`Rate limited. Retrying after ${delay}ms`);
    await this.sleep(delay);
    
    return this.client(originalRequest);
  }

  private async handleServerError(originalRequest: any): Promise<AxiosResponse> {
    originalRequest._retry = true;
    originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
    
    const delay = this.calculateBackoffDelay(originalRequest._retryCount);
    
    logger.warn(`Server error. Retry ${originalRequest._retryCount}/${this.retryConfig.maxRetries} after ${delay}ms`);
    await this.sleep(delay);
    
    return this.client(originalRequest);
  }

  private calculateBackoffDelay(attempt: number): number {
    const delay = this.retryConfig.baseDelay * Math.pow(2, attempt - 1);
    return Math.min(delay, this.retryConfig.maxDelay);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private formatError(error: any): ApiError {
    if (error.response) {
      return {
        message: error.response.data?.message || error.message || 'API request failed',
        status: error.response.status,
        code: error.response.data?.code,
      };
    } else if (error.request) {
      return {
        message: 'Network error - please check your connection',
        code: 'NETWORK_ERROR',
      };
    } else {
      return {
        message: error.message || 'Unknown error occurred',
        code: 'UNKNOWN_ERROR',
      };
    }
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}